import {
  applicantListResponseSchema,
  createPostingRequestSchema,
  postingEditConstraintsSchema,
  postingFeedQuerySchema,
  postingFeedResponseSchema,
  postingListResponseSchema,
  postingSchema,
  publicPostingFeedQuerySchema,
  updatePostingRequestSchema,
  type CreatePostingRequest,
  type PostingFeedQuery,
  type PublicPostingFeedQuery,
  type UpdatePostingRequest,
} from "@chotca/config";
import { z } from "zod";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

export function createPostingsResource(ctx: RequestContext) {
  return {
    /** Create a posting for an org. */
    createForOrg(orgId: string, body: CreatePostingRequest) {
      const payload = createPostingRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/postings`,
        body: payload,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    /** Worker job feed (F&B marketplace). */
    feed(query: Partial<PostingFeedQuery> = {}) {
      const q = postingFeedQuerySchema.parse({ sort: "NEWEST", ...query });
      // Array fields go through buildQuery as repeated params
      // (`?position=A&position=B`), which is what Nest's @Query() + zod
      // `z.array(...)` expects. Joining with "," produced a single string
      // and tripped a 400 validation_error.
      return requestJson(ctx, {
        method: "GET",
        path: "/postings/feed",
        query: {
          position: q.position?.length ? q.position : undefined,
          employmentType: q.employmentType?.length
            ? q.employmentType
            : undefined,
          venueType: q.venueType?.length ? q.venueType : undefined,
          benefits: q.benefits?.length ? q.benefits : undefined,
          skills: q.skills?.length ? q.skills : undefined,
          district: q.district,
          minSalary:
            q.minSalary !== undefined ? String(q.minSalary) : undefined,
          lat: q.lat,
          lng: q.lng,
          maxKm: q.maxKm,
          onlyEligible: q.onlyEligible ? true : undefined,
          orgId: q.orgId,
          state: q.state,
          sort: q.sort,
          q: q.q,
          cursor: q.cursor,
          limit: q.limit,
        },
        dataSchema: postingFeedResponseSchema,
        auth: true,
      });
    },

    /** Employer view: my postings across outlets. */
    listMine(cursor?: string, limit?: number) {
      const qs = new URLSearchParams();
      if (cursor) qs.set("cursor", cursor);
      if (limit) qs.set("limit", String(limit));
      const query = qs.toString();
      return requestJson(ctx, {
        method: "GET",
        path: `/me/postings${query ? `?${query}` : ""}`,
        dataSchema: postingListResponseSchema,
        auth: true,
      });
    },

    listByOrg(orgId: string, state?: string) {
      const qs = state ? `?state=${state}` : "";
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/postings${qs}`,
        dataSchema: postingListResponseSchema,
        auth: true,
      });
    },

    /**
     * Public, unauthenticated browse feed (web /viec-lam). A worker-free subset
     * of `feed()` — no GPS/match. Array fields go through as repeated query
     * params (`?position=A&position=B`), matching Nest's @Query() + zod array.
     */
    listPublic(query: Partial<PublicPostingFeedQuery> = {}) {
      const q = publicPostingFeedQuerySchema.parse({
        sort: "NEWEST",
        ...query,
      });
      return requestJson(ctx, {
        method: "GET",
        path: "/public/postings",
        query: {
          position: q.position?.length ? q.position : undefined,
          employmentType: q.employmentType?.length
            ? q.employmentType
            : undefined,
          venueType: q.venueType?.length ? q.venueType : undefined,
          benefits: q.benefits?.length ? q.benefits : undefined,
          skills: q.skills?.length ? q.skills : undefined,
          district: q.district,
          province: q.province,
          minSalary:
            q.minSalary !== undefined ? String(q.minSalary) : undefined,
          maxSalary:
            q.maxSalary !== undefined ? String(q.maxSalary) : undefined,
          salaryType: q.salaryType,
          orgId: q.orgId,
          state: q.state,
          sort: q.sort,
          q: q.q,
          cursor: q.cursor,
          page: q.page,
          limit: q.limit,
        },
        dataSchema: postingListResponseSchema,
        auth: false,
      });
    },

    /** Public OPEN postings of an org — used by worker-facing outlet detail. */
    listPublicByOrg(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/public/postings/by-org/${orgId}`,
        dataSchema: postingListResponseSchema,
        auth: false,
      });
    },

    getById(postingId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/postings/${postingId}`,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    /** Public, unauthenticated posting detail — used by SEO landing pages. */
    getPublicById(postingId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/public/postings/${postingId}`,
        dataSchema: postingSchema,
        auth: false,
      });
    },

    /** Public, unauthenticated list for sitemap generation. */
    listPublicForSitemap() {
      return requestJson(ctx, {
        method: "GET",
        path: `/public/postings/sitemap`,
        dataSchema: z.object({
          items: z.array(
            z.object({
              id: z.string(),
              title: z.string(),
              publishedAt: z.string(),
            }),
          ),
        }),
        auth: false,
      });
    },

    update(postingId: string, body: UpdatePostingRequest) {
      const payload = updatePostingRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/postings/${postingId}`,
        body: payload,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    /** Pre-edit constraints (locked locations, hire floor, editability). */
    getEditConstraints(postingId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/postings/${postingId}/edit-constraints`,
        dataSchema: postingEditConstraintsSchema,
        auth: true,
      });
    },

    listApplicants(postingId: string, cursor?: string) {
      const qs = cursor ? `?cursor=${cursor}` : "";
      return requestJson(ctx, {
        method: "GET",
        path: `/postings/${postingId}/applications${qs}`,
        dataSchema: applicantListResponseSchema,
        auth: true,
      });
    },

    close(postingId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/postings/${postingId}/close`,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    // Spend a plan boost to lift this posting into the feed's boosted tier.
    boost(postingId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/postings/${postingId}/boost`,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    cancel(postingId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/postings/${postingId}/cancel`,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    delete(postingId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/postings/${postingId}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },
  };
}
